'use client'

import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { CreditPackOffer } from '@/components/billing/credit-pack-offer'
import { useProductBrand } from '@/providers/product-brand-provider'

import { PlanNotice } from '@/components/billing/plan-notice'
import { planChangeNoticeKey } from '@/components/billing/plan-change-notice'
import { PricingPlanGrid } from '@/components/billing/pricing-plan-grid'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { useAuthContext } from '@/hooks/use-auth-context'
import { cn } from '@/lib/utils'
import {
  usePlans,
  useBillingProvider,
  useChatCostEstimate,
  useChangePlan,
  usePlanChangePreview,
  useCreateCheckout,
  useOpenInvoice,
  usePricingDefaultInterval,
  useSubscription,
} from '@/hooks/use-billing'
import { useBillingDialogStore } from '@/stores/billing-dialog-store'
import { usePartnerSupportPanel } from '@/hooks/use-partner-billing'
import { trackBeginCheckout } from '@/lib/analytics/billing-events'
import type { PricingDialogVariant } from '@/lib/pricing-dialog-variant'
import type { BillingInterval, PlanSummary } from '@/lib/billing-types'
import { chatCostLine } from '@/lib/chat-cost-estimate'

function microsToPrice(micros: number): number {
  return Math.round(micros / 1_000_000)
}

/**
 * The annual figures come off the PLAN, not off a discount formula.
 *
 * This used to be `Math.floor(monthly * 0.8) * 12` in four separate callbacks,
 * which is a second, client-side pricing model living next to the real one. It
 * disagreed with the database — the page quoted $468 for Starter while Stripe
 * charged $470.40 — and nothing in the code could have caught that, because the
 * stored `price_yearly_micros` was never read.
 *
 * `seed_plans.py` now stores whole-dollar annual prices that divide evenly by
 * 12, so reading them back gives both the annual total and the per-month figure
 * the toggle shows. Any future plan is priced in exactly one place.
 */
function yearlyTotal(plan: PlanSummary): number {
  return microsToPrice(plan.price_yearly_micros)
}

/**
 * What a co-branded workspace sees instead of the pricing cards.
 *
 * The partner owns the commercial relationship, so this replaces the plans
 * rather than sitting beside them, and it always offers a way forward: the
 * dialog is also the global 402 surface, so a customer can arrive here having
 * just run out of credits mid-task. Landing on "you cannot buy anything" with no
 * next step would be worse than the upgrade prompt it replaces.
 *
 * Every field falls back to generic copy — an operator can switch suppression on
 * without writing any — and the button is omitted entirely when no contact URL is
 * configured, since a button that goes nowhere is worse than none on a screen
 * whose only job is saying who to contact.
 */
function PartnerSupportPanel({
  panel,
}: {
  panel: NonNullable<ReturnType<typeof usePartnerSupportPanel>>
}) {
  const t = useTranslations('Billing')
  return (
    <div className="space-y-5 px-6 pt-7 pb-9 sm:px-9 sm:pt-9">
      <DialogHeader className="gap-2">
        <DialogTitle className="text-2xl font-semibold tracking-tight">
          {panel.headline || t('partnerSupportTitle')}
        </DialogTitle>
        <DialogDescription className="text-sm whitespace-pre-line">
          {panel.body || t('partnerSupportDescription')}
        </DialogDescription>
      </DialogHeader>
      {panel.ctaUrl && (
        <Button asChild className="w-full sm:w-auto">
          {/* The href is operator-configured and validated server-side to a
              mailto:/https: allowlist when the partner is saved. rel is set
              because an external target must not get a window handle back. */}
          <a href={panel.ctaUrl} target="_blank" rel="noopener noreferrer">
            {panel.ctaLabel || t('partnerSupportCta')}
          </a>
        </Button>
      )}
    </div>
  )
}

function PricingFrame({
  embedded,
  open,
  onOpenChange,
  children,
  className,
}: {
  embedded: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  className?: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {embedded ? (
        <section className="bg-card overflow-hidden rounded-2xl border">
          {children}
        </section>
      ) : (
        <DialogContent showCloseButton className={className}>
          {children}
        </DialogContent>
      )}
    </Dialog>
  )
}

function SubscriptionPricingDialog({
  variant = 'default',
  embedded = false,
  checkoutContext,
  intervalExperiment = true,
}: {
  variant?: PricingDialogVariant
  embedded?: boolean
  /**
   * Enter the `pricing-default-interval` experiment on this mount. Now ON by
   * default: every surface that shows a price is in the cohort.
   *
   * It used to be opt-in, and the cohort was exactly the public /tools and
   * /models studios. That excluded the in-product upgrade dialog — which is
   * where 13 of the 55 yearly checkouts in the 08-24..09-21 window came from,
   * all of them to zero purchases. The test could not see its own strongest
   * evidence, and a flag that only ever reaches the cheapest traffic cannot
   * answer a question about pricing.
   *
   * Still a prop rather than a hard-wired `true`, so a future surface can opt
   * out; nothing passes `false` today. Connect does not use this hatch — it is
   * excluded structurally by `inIntervalExperiment` below, because its mount
   * point would otherwise have to remember to pass the prop.
   */
  intervalExperiment?: boolean
  checkoutContext?: {
    successUrl: string
    cancelUrl: string
    source: 'connect'
    entry: string
    reason?: string
    initialInterval?: BillingInterval
  }
}) {
  const t = useTranslations('Billing')
  const tCommon = useTranslations('Common')
  // Use the unified auth context (not Clerk's `useAuth`): in self-auth mode
  // Clerk's hooks resolve to signed-out, which would disable `usePlans` and
  // render an empty modal with no upgradeable plans.
  const { isSignedIn, bootstrapSettled } = useAuthContext()
  const dialogOpen = useBillingDialogStore(s => s.isOpen)
  const reason = useBillingDialogStore(s => s.reason)
  const close = useBillingDialogStore(s => s.close)
  const isOpen = embedded || dialogOpen
  const [showAllPlans, setShowAllPlans] = React.useState(false)
  // Decided inside the component, not at the mount points: there are four of
  // them (the sidebar shell, the chrome-less connect-tools layout, and two
  // marketing tool pages), and a fifth would otherwise inherit nothing. The
  // marketing pages have no signed-in org, so this resolves to null there and
  // the normal plans render — correct for an anonymous visitor.
  const supportPanel = usePartnerSupportPanel()
  // The tools pages open this wall in front of someone who came to generate
  // an image or a video, so the plain "upgrade your plan" framing is replaced
  // with what a plan does for THAT goal: full-quality, watermark-free output.
  const titleKey =
    reason === 'exhausted'
      ? 'creditsExhaustedTitle'
      : reason === 'first_value'
        ? 'firstValueTitle'
        : reason === 'brand_limit'
          ? 'brandLimitTitle'
          : variant === 'tools'
            ? 'toolsUpgradeTitle'
            : 'upgradeTitle'
  const descriptionKey =
    reason === 'exhausted'
      ? 'exhaustedDescription'
      : reason === 'first_value'
        ? 'firstValueDescription'
        : reason === 'brand_limit'
          ? 'brandLimitDescription'
          : variant === 'tools'
            ? 'toolsUpgradeDescription'
            : 'upgradeDescription'
  const {
    data: plans,
    error: plansError,
    isValidating: plansValidating,
    mutate: retryPlans,
  } = usePlans({
    // Server-injected identity is available one render before the client token
    // manager has necessarily finished bootstrapping. Starting here during
    // that gap intermittently sent the first plans request without a usable
    // bearer token; opening the dialog again worked only because bootstrap had
    // finished by then.
    enabled: isSignedIn && bootstrapSettled,
  })
  // Only on the out-of-credits wall: that is where "how far does each plan go"
  // decides between upgrading and cancelling, and the answer is the customer's
  // own chats, not a generic credit count.
  const { data: chatCost } = useChatCostEstimate({
    enabled: reason === 'exhausted' && isOpen && isSignedIn && bootstrapSettled,
  })
  const creditsPerChat =
    reason === 'exhausted' && variant !== 'tools' && chatCost
      ? chatCost.creditsPerChat
      : null
  const { createCheckout, isMutating } = useCreateCheckout()
  const tBilling = useTranslations('SettingsPages.billing')
  const { data: billingProvider } = useBillingProvider({
    enabled: isSignedIn && bootstrapSettled,
  })
  const isShopifyBilled = billingProvider?.provider === 'shopify'
  const shopifyManageUrl = billingProvider?.manage_url ?? null
  const { changePlan, isMutating: isChanging } = useChangePlan()
  const { preview, isPreviewing } = usePlanChangePreview()
  // What the customer confirms before we charge the card on file. Upgrades do
  // not re-run Checkout — that is normal for a subscription — but charging on
  // a single click without showing the amount is not.
  const [pendingChange, setPendingChange] = React.useState<{
    plan: PlanSummary
    billingInterval: BillingInterval
    effective: string
    amountDueCents: number
    currency: string
  } | null>(null)
  // An org that already pays us must change plans in place, not through a
  // second Checkout — that opens a second live Stripe subscription and bills
  // them twice. `usePlanInfo` is the same source the billing page reads.
  const {
    data: currentSubscription,
    error: subscriptionError,
    isLoading: isSubscriptionLoading,
    mutate: revalidateSubscription,
  } = useSubscription({ enabled: isSignedIn && bootstrapSettled })
  const isSubscriptionUnavailable =
    isSubscriptionLoading ||
    subscriptionError !== undefined ||
    currentSubscription === undefined
  const hasLiveSubscription = !!currentSubscription?.has_stripe_customer
  // A failed renewal keeps the plan "effective" while Stripe retries the card,
  // so this org reads as subscribed and every plan click goes in place — where
  // the backend refuses it. Clicking the current plan to "renew" was the
  // customer-visible dead end; the open invoice is the only thing that fixes it.
  const isPastDue = currentSubscription?.status === 'past_due'
  const { data: openInvoice } = useOpenInvoice({
    enabled: isPastDue && isOpen && isSignedIn && bootstrapSettled,
  })
  // The invoice is paid in another tab and refetched on focus; the plan read
  // is not, so without this a paid customer keeps the past-due branch below.
  React.useEffect(() => {
    if (isPastDue && openInvoice === null) void revalidateSubscription()
  }, [isPastDue, openInvoice, revalidateSubscription])
  // No admin gate here. `POST /api/billing/checkout` takes RequireOrgMember,
  // so every member of the org can buy. This dialog is the global 402 surface —
  // it opens on the person who just ran out mid-task — and telling that person
  // to go find an admin was a dead end, not a next step. Upgrading only ever
  // adds capacity; the destructive actions (Stripe portal, cancel) are still
  // admin-only, and are not reachable from here.
  // The `pricing-default-interval` arm, asked for only while this dialog is
  // actually open on a marketing studio: the exposure is seeing a price, not
  // having the dialog mounted (it mounts on every authenticated layout).
  //
  // Connect is excluded HERE, not only at `defaultBillingCycle`. Its interval
  // comes from `checkoutContext.initialInterval`, so a Connect visitor cannot
  // receive the treatment — but asking anyway would still persist an arm for
  // their org and tag their `begin_checkout` with it, putting orgs into the
  // cohort whose interval was decided by a URL parameter. That is worse than
  // not measuring them: a control-assigned org would be counted as having seen
  // yearly while looking at monthly.
  const inIntervalExperiment = intervalExperiment && variant !== 'connect'
  const { data: intervalArm, settled: intervalArmSettled } =
    usePricingDefaultInterval({
      enabled: inIntervalExperiment && isOpen && isSignedIn && bootstrapSettled,
    })
  // Connect has its own preselection contract and is outside the experiment.
  //
  // Everywhere else the fallback is monthly, and the fallback is what an
  // anonymous visitor gets: arms are dealt per organization, so nobody signed
  // out is in one. It used to be yearly, which is how a visitor who came to
  // make one clip met a $180 charge on the Stripe page — 55 such checkouts in
  // four weeks, none of which ever paid, against 10 purchases on monthly.
  // The server says the same thing for the same reason
  // (`packages/services/experiments/pricing_default.py`); the two defaults must
  // not drift, or a slow arm response would flip the toggle under the user.
  const defaultBillingCycle: BillingInterval =
    variant === 'connect'
      ? (checkoutContext?.initialInterval ?? 'monthly')
      : (intervalArm?.interval ?? 'monthly')
  // Only the user's own toggle is state. Deriving the rest means an arm that
  // arrives a beat late cannot fight a `useEffect` for the same variable, and
  // a choice already made is never overwritten by the experiment's default.
  const [pickedBillingCycle, setPickedBillingCycle] =
    React.useState<BillingInterval | null>(null)
  const billingCycle = pickedBillingCycle ?? defaultBillingCycle
  const setBillingCycle = setPickedBillingCycle
  // The prices and the toggle are BOTH the treatment, so neither may render in
  // the control position and then flip: that shows both arms to the test arm,
  // and a buy button on yearly prices is actionable while it is up. The toggle
  // row and the plan grid below are therefore both withheld until the arm
  // settles — see the grid's comment for why it cannot rely on its own load
  // state to cover this window.
  const intervalPending =
    inIntervalExperiment &&
    isSignedIn &&
    bootstrapSettled &&
    !intervalArmSettled
  const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(
    null,
  )
  // An in-place plan change never leaves the page, so unlike a Checkout
  // redirect it has to say what happened where the user already is.
  // Failure only. Success closes this dialog and reports in its own modal, so
  // a "success" tone here would be dead code pretending to be a state.
  const [changeNotice, setChangeNotice] = React.useState<string | null>(null)
  // Shown after a successful change, in place of the confirmation. The plan
  // grid closes behind it: leaving a "pick a plan" grid open after someone has
  // just picked one invites a second, accidental change — and on a surface
  // that charges a card, that is not a small mistake.
  //
  // Not a toast: this dialog also mounts on the marketing tool pages, which
  // render no <Toaster/>, so a toast would be invisible exactly where most
  // upgrades happen.
  const [changeResult, setChangeResult] = React.useState<string | null>(null)

  // Amount charged now (annual total for yearly), matching the displayed price.
  const chargeNow = React.useCallback(
    (plan: PlanSummary): number => {
      return billingCycle === 'yearly'
        ? yearlyTotal(plan)
        : microsToPrice(plan.price_monthly_micros)
    },
    [billingCycle],
  )

  const handlePlanSelect = React.useCallback(
    async (plan: PlanSummary) => {
      if (isSubscriptionUnavailable) return
      if (plan.slug === 'enterprise') {
        window.open(
          'mailto:founders@soku.ai?subject=Enterprise%20Plan%20Inquiry',
          '_blank',
        )
        return
      }
      // Clear a previous attempt's failure so a stale red band never sits
      // over a fresh click.
      setChangeNotice(null)
      if (
        isPastDue &&
        plan.id === currentSubscription?.plan_id &&
        billingCycle === currentSubscription.billing_interval
      ) {
        // Renewing the plan they already have means paying the invoice that
        // failed. Opened before any await so it stays a user-initiated popup.
        if (openInvoice) {
          window.open(openInvoice.hosted_invoice_url, '_blank', 'noopener')
        } else {
          setChangeNotice(t('planChangeWhilePastDue'))
        }
        return
      }
      const value = chargeNow(plan)
      trackBeginCheckout({
        slug: plan.slug,
        name: plan.name,
        interval: billingCycle,
        value,
        // Which arm produced this checkout, and whether the interval on it is
        // the one the arm preselected or one the visitor switched to. Without
        // the second flag a lifted monthly share cannot be told apart from
        // people who would have picked monthly anyway.
        ...(inIntervalExperiment
          ? {
              pricingDefaultVariant: intervalArm?.variant ?? 'unassigned',
              pricingDefaultInterval: defaultBillingCycle,
              pricingIntervalSwitched: billingCycle !== defaultBillingCycle,
            }
          : {}),
        ...(checkoutContext
          ? {
              source: checkoutContext.source,
              entry: checkoutContext.entry,
              reason: checkoutContext.reason,
            }
          : {}),
      })
      setSelectedPlanId(plan.id)

      // Already paying: change the existing subscription in place. Routing
      // this through Checkout would open a SECOND live Stripe subscription —
      // double billing, and a provisioning failure on our side.
      if (hasLiveSubscription) {
        try {
          const quote = await preview(plan.id, billingCycle)
          setPendingChange({
            plan,
            billingInterval: billingCycle,
            effective: quote?.effective ?? 'immediate',
            amountDueCents: quote?.amount_due_cents ?? 0,
            currency: quote?.currency ?? 'usd',
          })
        } catch (error) {
          setChangeNotice(t(planChangeNoticeKey(error)))
        } finally {
          setSelectedPlanId(null)
        }
        return
      }

      // A failed Checkout has no redirect to speak for it. Until this branch
      // existed the customer got nothing at all: `handleApiError` toasts only
      // on 500, and this dialog also mounts on marketing pages with no
      // `<Toaster/>`, so a 400 left a button that simply did nothing.
      const outcome = await createCheckout(plan.id, billingCycle, {
        planSlug: plan.slug,
        value,
        ...checkoutContext,
      })
      if (!outcome.ok) {
        // "Please try again" is wrong advice for someone whose card was just
        // charged, so the two payment-in-flight codes get their own copy.
        setChangeNotice(
          t(
            outcome.code === 'checkout_awaiting_payment'
              ? 'checkoutAwaitingPayment'
              : outcome.code === 'checkout_settling'
                ? 'checkoutSettling'
                : 'checkoutFailed',
          ),
        )
        setSelectedPlanId(null)
      }
    },
    [
      billingCycle,
      checkoutContext,
      chargeNow,
      createCheckout,
      currentSubscription,
      defaultBillingCycle,
      hasLiveSubscription,
      inIntervalExperiment,
      intervalArm?.variant,
      isPastDue,
      isSubscriptionUnavailable,
      openInvoice,
      preview,
      t,
    ],
  )

  const confirmPlanChange = React.useCallback(async () => {
    if (!pendingChange) return
    const { plan, billingInterval, effective } = pendingChange
    try {
      await changePlan(plan.id, billingInterval)
      setPendingChange(null)
      setChangeResult(
        effective === 'period_end'
          ? t('downgradeScheduled', { plan: plan.name })
          : t('upgradeApplied', { plan: plan.name }),
      )
      // The grid has served its purpose; the result modal stays.
      close()
    } catch (error) {
      // Keep the plan grid open on failure — the customer is still mid-task
      // and needs somewhere to retry from.
      setPendingChange(null)
      setChangeNotice(t(planChangeNoticeKey(error)))
    }
  }, [changePlan, close, pendingChange, t])

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        // This dialog is mounted persistently, so its state outlives every
        // close. Without this reset the next person to open it is greeted by
        // the previous attempt's failure before they have clicked anything.
        setChangeNotice(null)
        close()
      }
    },
    [close],
  )

  const sortedPlans = React.useMemo(() => {
    if (!plans) return []
    return [...plans].sort((a, b) => a.sort_order - b.sort_order)
  }, [plans])

  // A Shopify-billed workspace has no Soku-side checkout to offer: every
  // payment endpoint 403s for it, and the plan is chosen on Shopify's hosted
  // page. Same shape as the managed-workspace branch below — the backend is
  // the rule, this is the surface.
  if (isShopifyBilled) {
    return (
      <PricingFrame
        embedded={embedded}
        open={isOpen}
        onOpenChange={handleOpenChange}
        className="w-[95vw] max-w-lg gap-0 overflow-y-auto p-0"
      >
        <div className="space-y-4 px-6 py-8 sm:px-9">
          <DialogHeader className="gap-2">
            <DialogTitle className="text-xl font-semibold tracking-tight">
              {tBilling('shopify.billedByShopify')}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {tBilling('shopify.billedByShopifyDescription')}
            </DialogDescription>
          </DialogHeader>
          {shopifyManageUrl ? (
            <Button asChild className="w-full">
              <a
                href={shopifyManageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {tBilling('shopify.managePlan')}
              </a>
            </Button>
          ) : null}
        </div>
      </PricingFrame>
    )
  }

  // A managed workspace never sees plans or a checkout button, on any of the
  // four mount points. The backend refuses checkout independently
  // (RequireSelfServeBilling) — this is the surface, not the rule.
  if (supportPanel) {
    return (
      <PricingFrame
        embedded={embedded}
        open={isOpen}
        onOpenChange={handleOpenChange}
        className="w-[95vw] max-w-lg gap-0 overflow-y-auto p-0"
      >
        <PartnerSupportPanel panel={supportPanel} />
      </PricingFrame>
    )
  }

  return (
    <>
      {/* Its own modal, layered over the plan grid. An inline strip was too
          easy to miss for something that charges a card, and it competed with
          the grid instead of interrupting it. The amount comes from Stripe's
          invoice preview, so what is confirmed is what gets billed. */}
      <Dialog
        open={pendingChange !== null || changeResult !== null}
        onOpenChange={next => {
          if (next || isChanging) return
          setPendingChange(null)
          setChangeResult(null)
        }}
      >
        <DialogContent showCloseButton className="w-[95vw] max-w-md">
          {changeResult ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('planChangedTitle')}</DialogTitle>
                <DialogDescription>{changeResult}</DialogDescription>
              </DialogHeader>
              <div className="mt-2 flex justify-end">
                <Button onClick={() => setChangeResult(null)}>
                  {t('planChangedDone')}
                </Button>
              </div>
            </>
          ) : pendingChange ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {pendingChange.effective === 'period_end'
                    ? t('confirmDowngradeTitle', {
                        plan: pendingChange.plan.name,
                      })
                    : t('confirmUpgradeTitle', {
                        plan: pendingChange.plan.name,
                      })}
                </DialogTitle>
                <DialogDescription>
                  {pendingChange.effective === 'period_end'
                    ? t('confirmDowngradeBody', {
                        plan: pendingChange.plan.name,
                      })
                    : t('confirmUpgradeBody', {
                        plan: pendingChange.plan.name,
                        amount: `$${(pendingChange.amountDueCents / 100).toFixed(2)}`,
                      })}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={isChanging}
                  onClick={() => setPendingChange(null)}
                >
                  {t('cancelChange')}
                </Button>
                <Button
                  disabled={isChanging}
                  onClick={() => void confirmPlanChange()}
                >
                  {isChanging && (
                    <Loader2 className="me-2 size-4 animate-spin" />
                  )}
                  {t('confirmChange')}
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <PricingFrame
        embedded={embedded}
        open={isOpen}
        onOpenChange={handleOpenChange}
        className={cn(
          'max-h-[94vh] w-[95vw] gap-0 overflow-y-auto p-0',
          variant === 'connect' && !showAllPlans ? 'max-w-lg' : 'max-w-7xl',
        )}
      >
        <div className="space-y-4 px-6 pt-6 pb-4 sm:px-9 sm:pt-7">
          <DialogHeader className="gap-1.5">
            <DialogTitle className="text-2xl font-semibold tracking-tight">
              {variant === 'connect'
                ? 'Go beyond the built-in trial with Creator'
                : t(titleKey)}
            </DialogTitle>
            <DialogDescription className="max-w-3xl text-sm leading-relaxed">
              {variant === 'connect'
                ? 'Without a paid plan, built-in calls are limited to 50 credits each ' +
                  'and 200 in total, with enough balance. Upgrade to continue beyond ' +
                  'these limits. Credits are quoted before each call.'
                : t(descriptionKey)}
              {creditsPerChat && chatCost ? (
                <span className="text-muted-foreground mt-1 block">
                  {chatCostLine(chatCost, t)}
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="flex h-6 items-center justify-center gap-2.5">
            {intervalPending ? null : (
              <>
                <span
                  className={cn(
                    'text-sm font-medium transition-colors',
                    billingCycle === 'monthly'
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {tCommon('monthly')}
                </span>
                <Switch
                  checked={billingCycle === 'yearly'}
                  onCheckedChange={checked =>
                    setBillingCycle(checked ? 'yearly' : 'monthly')
                  }
                  aria-label="Toggle yearly billing"
                />
                <span
                  className={cn(
                    'text-sm font-medium transition-colors',
                    billingCycle === 'yearly'
                      ? 'text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {t('yearly')}
                </span>
                {/* "Save 20%" is a fact — it is the gap between the stored
                  monthly and yearly prices, and the grid prints the dollar
                  amount next to it. A "Limited-time offer" badge used to sit
                  here too and was removed on 2026-09-22: nothing in the code,
                  the plans table or Stripe ever gave that offer an end date,
                  so it was a permanent countdown to nothing. Do not restore it
                  without a real deadline behind it. */}
                {variant !== 'connect' && (
                  <span className="ms-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                    {t('save20')}
                  </span>
                )}
              </>
            )}
          </div>

          {isPastDue && openInvoice ? (
            <div
              data-testid="past-due-invoice"
              className="flex flex-col items-center justify-center gap-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2.5 text-center text-sm text-amber-900 dark:text-amber-300 sm:flex-row sm:text-start"
            >
              <span>
                {t('pastDueInvoiceNotice', {
                  plan: currentSubscription.plan_name,
                })}
              </span>
              <Button asChild size="sm" className="shrink-0">
                <a
                  href={openInvoice.hosted_invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('payOpenInvoice', {
                    amount: new Intl.NumberFormat(undefined, {
                      style: 'currency',
                      currency: openInvoice.currency.toUpperCase(),
                    }).format(openInvoice.amount_due_cents / 100),
                  })}
                </a>
              </Button>
            </div>
          ) : null}

          <PlanNotice message={changeNotice} />
        </div>

        {/* Plans not renderable yet: never show a silently empty grid — the
          dialog otherwise looks broken with nothing to pick. Spinner only
          while the fetch is in flight; a failed fetch and a successful but
          empty plan list (misconfigured backend) both get the text state.

          `intervalPending` joins that condition because the PRICES are the
          treatment, not just the toggle. `usePlans` starts on mount, not on
          open, so by the time anyone clicks upgrade the plans are usually
          already cached and this grid would paint instantly — showing a
          test-arm visitor yearly prices, and an enabled buy button, for as
          long as their arm takes to arrive. Hiding the toggle alone left the
          more consequential half on screen. */}
        {intervalPending ||
        (variant === 'connect' && !showAllPlans
          ? sortedPlans.filter(plan => plan.slug === 'creator')
          : sortedPlans
        ).length === 0 ? (
          <div className="flex items-center justify-center px-6 pt-2 pb-16 md:px-9">
            {intervalPending ||
            !bootstrapSettled ||
            (!plans && (!plansError || plansValidating)) ? (
              <Loader2 className="text-muted-foreground size-6 animate-spin" />
            ) : (
              <div className="flex flex-col items-center gap-3">
                <p className="text-muted-foreground text-sm">
                  {t('plansLoadFailed')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void retryPlans()}
                >
                  {tCommon('retry')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <PricingPlanGrid
            plans={
              variant === 'connect' && !showAllPlans
                ? sortedPlans.filter(plan => plan.slug === 'creator')
                : sortedPlans
            }
            billingCycle={billingCycle}
            variant={variant}
            chargesFullTermToday={
              !isSubscriptionUnavailable && !hasLiveSubscription
            }
            creditsPerChat={creditsPerChat}
            selectedPlanId={isMutating ? selectedPlanId : null}
            disabled={
              isSubscriptionUnavailable ||
              isMutating ||
              isChanging ||
              isPreviewing
            }
            onPlanSelect={plan => void handlePlanSelect(plan)}
          />
        )}

        {variant === 'connect' && (
          <div className="pb-5 text-center">
            <Button
              variant="link"
              onClick={() => setShowAllPlans(value => !value)}
            >
              {showAllPlans ? 'Back to Creator' : 'Compare other plans'}
            </Button>
          </div>
        )}
        <div className="border-border/60 border-t px-6 py-3.5 sm:px-9">
          <p className="text-muted-foreground text-center text-xs">
            {t('guarantee')}
          </p>
        </div>
      </PricingFrame>
    </>
  )
}

export function PricingDialog(
  props: React.ComponentProps<typeof SubscriptionPricingDialog>,
) {
  const { product } = useProductBrand()
  return product === 'toolfare' ? (
    <ToolfarePricingDialog {...props} />
  ) : (
    <SubscriptionPricingDialog {...props} />
  )
}

function ToolfarePricingDialog(
  props: React.ComponentProps<typeof SubscriptionPricingDialog>,
) {
  const open = useBillingDialogStore(s => s.isOpen)
  const close = useBillingDialogStore(s => s.close)
  const partner = usePartnerSupportPanel()
  const { isSignedIn, bootstrapSettled, startSignUp } = useAuthContext()
  const provider = useBillingProvider({
    enabled: isSignedIn && bootstrapSettled,
  })
  const successUrl = useBillingDialogStore(s => s.successUrl)
  const cancelUrl = useBillingDialogStore(s => s.cancelUrl)
  const returnUrls =
    props.checkoutContext ??
    (successUrl && cancelUrl ? { successUrl, cancelUrl } : undefined)
  // Keep the existing managed-billing gates; these never sell Stripe plans.
  if (
    isSignedIn &&
    bootstrapSettled &&
    (partner || provider.data?.provider === 'shopify')
  ) {
    return <SubscriptionPricingDialog {...props} />
  }
  return (
    <PricingFrame
      embedded={props.embedded ?? false}
      open={!!props.embedded || open}
      onOpenChange={value => {
        if (!value) close()
      }}
      className="max-w-lg"
    >
      <DialogHeader className="sr-only">
        <DialogTitle>Buy toolfare credits</DialogTitle>
        <DialogDescription>Pay as you go credits</DialogDescription>
      </DialogHeader>
      {!bootstrapSettled ? (
        <Loader2 aria-label="Loading account" className="animate-spin" />
      ) : !isSignedIn ? (
        <div className="space-y-4 p-6">
          <p>Sign in to buy toolfare credits.</p>
          <Button onClick={() => startSignUp(window.location.href)}>
            Sign in
          </Button>
        </div>
      ) : provider.error ? (
        <div className="space-y-4 p-6">
          <p>We couldn’t check your billing options.</p>
          <Button onClick={() => void provider.mutate()}>Try again</Button>
        </div>
      ) : !provider.data ? (
        <Loader2
          aria-label="Loading billing options"
          className="animate-spin"
        />
      ) : (
        <CreditPackOffer returnUrls={returnUrls} />
      )}
    </PricingFrame>
  )
}
